/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react'
import AppDialog, { ModalActions } from '@/components/CustomModal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { Switch } from '@/components/ui/switch'
import {
  useCommitCustomersImportMutation,
  usePreviewCustomersImportMutation,
  type CustomerImportOptions,
  type CustomerImportPreview,
} from '@/hooks/useCustomerImport'

export function ImportCustomersDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  const previewMut = usePreviewCustomersImportMutation()
  const commitMut = useCommitCustomersImportMutation()

  const [file, setFile] = useState<File | null>(null)
  const [delimiter, setDelimiter] = useState(',')
  const [updateIfExists, setUpdateIfExists] = useState(true)

  const [columns, setColumns] = useState({
    name: 'name',
    contact: 'contact',
    phone: 'phone',
    email: 'email',
  })

  const options = useMemo<CustomerImportOptions>(() => {
    return {
      delimiter,
      updateIfExists,
      columns: {
        name: columns.name || undefined,
        contact: columns.contact || undefined,
        phone: columns.phone || undefined,
        email: columns.email || undefined,
      },
    }
  }, [delimiter, updateIfExists, columns])

  const preview = previewMut.data as CustomerImportPreview | undefined

  // ✅ si l'user change un paramètre, le preview n'est plus fiable
  useEffect(() => {
    if (previewMut.data) previewMut.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    file,
    delimiter,
    updateIfExists,
    columns.name,
    columns.contact,
    columns.phone,
    columns.email,
  ])

  const canPreview = !!file && !previewMut.isPending && !commitMut.isPending
  const canCommit =
    !!file &&
    !!preview &&
    (preview.summary?.invalid ?? 0) === 0 &&
    !commitMut.isPending &&
    !previewMut.isPending

  const resetAll = () => {
    setFile(null)
    previewMut.reset()
    commitMut.reset()
  }

  const doPreview = async () => {
    if (!file) return
    try {
      const res = await previewMut.mutateAsync({ file, options })
      toast.success('Preview OK', {
        description: `${res.summary.total} lignes — ${res.summary.valid} OK — ${res.summary.invalid} erreurs — create ${res.summary.willCreate} / update ${res.summary.willUpdate}`,
      })
    } catch (e: any) {
      toast.error('Preview échoué', {
        description: e?.response?.data?.message ?? 'Erreur inconnue',
      })
    }
  }

  const doCommit = async () => {
    if (!file) return

    if (!preview) {
      toast.error('Faites un preview avant d’importer')
      return
    }
    if ((preview.summary?.invalid ?? 0) > 0) {
      toast.error('Corrigez les erreurs avant import')
      return
    }

    try {
      const res = await commitMut.mutateAsync({ file, options })
      toast.success('Import terminé', {
        description: `${res.created} créés — ${res.updated} mis à jour — ${res.skipped} ignorés`,
      })
      onOpenChange(false)
      resetAll()
    } catch (e: any) {
      toast.error('Import échoué', {
        description: e?.response?.data?.message ?? 'Erreur inconnue',
      })
    }
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v)
        if (!v) resetAll()
      }}
      title='Importer clients (CSV)'
      description='Faites un preview, corrigez, puis importez.'
      footer={
        <div className='flex items-center gap-2'>
          <Button
            type='button'
            variant='secondary'
            disabled={!canPreview}
            onClick={doPreview}
          >
            Preview
          </Button>

          <ModalActions
            onConfirm={doCommit}
            confirmLabel='Importer'
            confirmLoading={commitMut.isPending}
            confirmDisabled={!canCommit}
          />
        </div>
      }
    >
      <div className='px-4 py-2 space-y-4'>
        <div className='grid gap-1.5'>
          <Label>Fichier CSV</Label>
          <Input
            type='file'
            accept='.csv,text/csv'
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>

        <div className='grid grid-cols-2 gap-3'>
          <div className='grid gap-1.5'>
            <Label>Délimiteur</Label>
            <Input
              value={delimiter}
              onChange={(e) => setDelimiter(e.target.value)}
            />
          </div>
        </div>

        <div className='flex items-center justify-between rounded-md border p-3'>
          <div className='space-y-0.5'>
            <div className='text-sm font-medium'>Mettre à jour si existe</div>
            <div className='text-xs text-muted-foreground'>
              Si un client correspond (email/téléphone), on le met à jour.
            </div>
          </div>
          <Switch
            checked={updateIfExists}
            onCheckedChange={setUpdateIfExists}
          />
        </div>

        <div className='rounded-md border p-3 space-y-3'>
          <div className='text-sm font-medium'>Colonnes (optionnel)</div>
          <div className='grid grid-cols-2 gap-3'>
            <Field label='name'>
              <Input
                value={columns.name}
                onChange={(e) =>
                  setColumns((c) => ({ ...c, name: e.target.value }))
                }
              />
            </Field>
            <Field label='contact'>
              <Input
                value={columns.contact}
                onChange={(e) =>
                  setColumns((c) => ({ ...c, contact: e.target.value }))
                }
              />
            </Field>
            <Field label='phone'>
              <Input
                value={columns.phone}
                onChange={(e) =>
                  setColumns((c) => ({ ...c, phone: e.target.value }))
                }
              />
            </Field>
            <Field label='email'>
              <Input
                value={columns.email}
                onChange={(e) =>
                  setColumns((c) => ({ ...c, email: e.target.value }))
                }
              />
            </Field>
          </div>
        </div>

        {preview ? (
          <div className='rounded-md border p-3 text-sm space-y-2'>
            <div className='font-medium'>Résumé preview</div>
            <div className='text-muted-foreground'>
              Total: <b>{preview.summary.total}</b> — OK:{' '}
              <b>{preview.summary.valid}</b> — Erreurs:{' '}
              <b>{preview.summary.invalid}</b>
              <br />
              Create: <b>{preview.summary.willCreate}</b> — Update:{' '}
              <b>{preview.summary.willUpdate}</b> — Skip:{' '}
              <b>{preview.summary.willSkip}</b>
            </div>

            {(preview.rows?.length ?? 0) > 0 ? (
              <>
                {/* extrait erreurs */}
                {preview.rows.some((r) => r.status === 'KO') ? (
                  <div className='mt-2'>
                    <div className='font-medium text-red-600'>
                      Erreurs (extrait)
                    </div>
                    <ul className='list-disc pl-5 text-xs'>
                      {preview.rows
                        .filter((r) => r.status === 'KO')
                        .slice(0, 10)
                        .map((r) => (
                          <li key={r.line}>
                            L{r.line}:{' '}
                            {(r.errors ?? [])
                              .map((e) => e.message)
                              .filter(Boolean)
                              .join(' | ') || 'Erreur'}
                          </li>
                        ))}
                    </ul>
                  </div>
                ) : (
                  <div className='text-xs text-green-700'>
                    Aucune erreur bloquante.
                  </div>
                )}
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </AppDialog>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className='grid gap-1.5'>
      <Label>{label}</Label>
      {children}
    </div>
  )
}
